import React, { lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// ─── Critical path: Homepage cargada de forma eager ───────────────────────────
import HomePage from "./pages/HomePage";

// ─── Public pages: lazy (code splitting por ruta) ─────────────────────────────
const LandingPage                 = lazy(() => import("./pages/LandingPage"));
const PreciosPage                 = lazy(() => import("./pages/PreciosPage"));
const FuncionalidadesPage         = lazy(() => import("./pages/FuncionalidadesPage"));
const InteligenciaArtificialPage  = lazy(() => import("./pages/InteligenciaArtificialPage"));
const ParaQuienPage               = lazy(() => import("./pages/ParaQuienPage"));
const Blog                        = lazy(() => import("./pages/Blog"));
const BlogPost                    = lazy(() => import("./pages/BlogPost"));
const ContactoPage                = lazy(() => import("./pages/ContactoPage"));
const DemoPage                    = lazy(() => import("./pages/DemoPage"));
const RecursosPage                = lazy(() => import("./pages/RecursosPage"));
const SobreNosotrosPage           = lazy(() => import("./pages/SobreNosotrosPage"));

// ─── Sector landing pages: lazy ───────────────────────────────────────────────
const SoftwareReformasPage        = lazy(() => import("./pages/sectors/SoftwareReformasPage"));
const ERPConstruccionPage         = lazy(() => import("./pages/sectors/ERPConstruccionPage"));
const SoftwareMantenimientoPage   = lazy(() => import("./pages/sectors/SoftwareMantenimientoPage"));
const SoftwareFontanerosPage      = lazy(() => import("./pages/sectors/SoftwareFontanerosPage"));
const SoftwareElectricistasPage   = lazy(() => import("./pages/sectors/SoftwareElectricistasPage"));
const SoftwareReformasBarcelonaPage = lazy(() => import("./pages/sectors/SoftwareReformasBarcelonaPage"));
const SoftwareReformasMadridPage  = lazy(() => import("./pages/sectors/SoftwareReformasMadridPage"));

// ─── Admin pages: lazy (chunk separado, no llega a usuarios públicos) ─────────
const AdminLogin             = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout            = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard         = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminHero              = lazy(() => import("./pages/admin/AdminHero"));
const AdminCarousel          = lazy(() => import("./pages/admin/AdminCarousel"));
const AdminSections          = lazy(() => import("./pages/admin/AdminSections"));
const AdminSectionsAdvanced  = lazy(() => import("./pages/admin/AdminSectionsAdvanced"));
const AdminSectionEditor     = lazy(() => import("./pages/admin/AdminSectionEditor"));
const AdminLeads             = lazy(() => import("./pages/admin/AdminLeads"));
const AdminAnalytics         = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminSEO               = lazy(() => import("./pages/admin/AdminSEO"));
const AdminLegal             = lazy(() => import("./pages/admin/AdminLegal"));
const AdminSettings          = lazy(() => import("./pages/admin/AdminSettings"));
const AdminConversions       = lazy(() => import("./pages/admin/AdminConversions"));
const AdminTestimonials      = lazy(() => import("./pages/admin/AdminTestimonials"));
const AdminBlog              = lazy(() => import("./pages/admin/AdminBlog"));
const AdminBlogEditor        = lazy(() => import("./pages/admin/AdminBlogEditor"));
const AdminAutomations       = lazy(() => import("./pages/admin/AdminAutomations"));
const AdminGlobalSettings    = lazy(() => import("./pages/admin/AdminGlobalSettings"));
const AdminPagesList         = lazy(() => import("./pages/admin/AdminPagesList"));

// ─── Fallback mínimo mientras carga el chunk ──────────────────────────────────
const PageSkeleton = () => (
  <div
    style={{ minHeight: "100vh", background: "#080d1a" }}
    aria-hidden="true"
  />
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            {/* Public pages */}
            <Route path="/"                       element={<HomePage />} />
            <Route path="/inicio"                 element={<LandingPage />} />
            <Route path="/funcionalidades"        element={<FuncionalidadesPage />} />
            <Route path="/inteligencia-artificial" element={<InteligenciaArtificialPage />} />
            <Route path="/precios"                element={<PreciosPage />} />
            <Route path="/para-quien"             element={<ParaQuienPage />} />
            <Route path="/blog"                   element={<Blog />} />
            <Route path="/blog/:slug"             element={<BlogPost />} />
            <Route path="/contacto"               element={<ContactoPage />} />
            <Route path="/demo"                   element={<DemoPage />} />
            <Route path="/recursos"               element={<RecursosPage />} />
            <Route path="/sobre-nosotros"         element={<SobreNosotrosPage />} />

            {/* Sector landing pages */}
            <Route path="/software-reformas"          element={<SoftwareReformasPage />} />
            <Route path="/erp-construccion"           element={<ERPConstruccionPage />} />
            <Route path="/software-mantenimiento"     element={<SoftwareMantenimientoPage />} />
            <Route path="/software-fontaneros"        element={<SoftwareFontanerosPage />} />
            <Route path="/software-electricistas"     element={<SoftwareElectricistasPage />} />
            <Route path="/software-reformas-barcelona" element={<SoftwareReformasBarcelonaPage />} />
            <Route path="/software-reformas-madrid"   element={<SoftwareReformasMadridPage />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route path="dashboard"                         element={<AdminDashboard />} />
              <Route path="blog"                              element={<AdminBlog />} />
              <Route path="blog/:postId"                      element={<AdminBlogEditor />} />
              <Route path="hero"                              element={<AdminHero />} />
              <Route path="carousel"                          element={<AdminCarousel />} />
              <Route path="sections"                          element={<AdminSections />} />
              <Route path="sections-advanced"                 element={<AdminSectionsAdvanced />} />
              <Route path="sections-advanced/:sectionId"      element={<AdminSectionEditor />} />
              <Route path="testimonials"                      element={<AdminTestimonials />} />
              <Route path="conversions"                       element={<AdminConversions />} />
              <Route path="leads"                             element={<AdminLeads />} />
              <Route path="analytics"                         element={<AdminAnalytics />} />
              <Route path="automations"                       element={<AdminAutomations />} />
              <Route path="global-settings"                   element={<AdminGlobalSettings />} />
              <Route path="pages"                             element={<AdminPagesList />} />
              <Route path="seo"                               element={<AdminSEO />} />
              <Route path="legal"                             element={<AdminLegal />} />
              <Route path="settings"                          element={<AdminSettings />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
